import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import * as Routes from '@/modules/routes';
import SPAPill from '../../shared/SPAPill';
import LessonGuideActions from './LessonGuideActions';
import { LessonGuide, SlideShowParent, guideTitle } from './useLessonGuides';

const columnHelper = createColumnHelper<LessonGuide>();

const linkClass = 'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline tw-transition-colors';

const Muted = ({ children }: { children: React.ReactNode }) => (
  <span className="tw-text-spa-tertiary">{children}</span>
);

// The legacy table's columns (admin/slide_shows/_slide_show). Nothing sorts: the order is the
// drag order. Title and question count open the legacy slideshow pages.
const getLessonGuideColumns = (programId: number, parent: SlideShowParent): ColumnDef<LessonGuide, unknown>[] => {
  const parentParams = { parent_id: parent.id, parent_type: parent.type, program_id: programId };

  return [
    columnHelper.display({
      cell: info => <Muted>{info.row.index + 1}</Muted>,
      enableSorting: false,
      header: '#',
      id: 'number',
      size: 40,
    }),
    columnHelper.accessor('title', {
      cell: info => (
        <a className={linkClass} href={Routes.admin_slide_show_path(info.row.original.id, parentParams)}>
          {guideTitle(info.row.original)}
        </a>
      ),
      enableSorting: false,
      header: 'Title',
      size: 320,
    }),
    columnHelper.accessor('visible', {
      cell: info => (
        <SPAPill tone={info.getValue() ? 'success' : 'neutral'}>
          {info.getValue() ? 'Published' : 'Unpublished'}
        </SPAPill>
      ),
      enableSorting: false,
      header: 'Status',
      size: 110,
    }),
    columnHelper.accessor('archived', {
      cell: info => (info.getValue() ? 'Yes' : <Muted>No</Muted>),
      enableSorting: false,
      header: 'Archived',
      size: 80,
    }),
    columnHelper.accessor('slides_count', {
      cell: info => <span className="tw-tabular-nums">{info.getValue()}</span>,
      enableSorting: false,
      header: 'Slides',
      size: 80,
    }),
    columnHelper.accessor('question_groups_count', {
      cell: (info) => {
        const { id, questions_on_slides_count: onSlides } = info.row.original;
        return (
          <span className="tw-whitespace-nowrap tw-tabular-nums">
            <a className={linkClass} href={Routes.questions_shared_slide_show_path(id, parentParams)}>
              {info.getValue()}
            </a>
            <Muted>{` · ${onSlides} on slides`}</Muted>
          </span>
        );
      },
      enableSorting: false,
      header: 'Questions',
      size: 140,
    }),
    columnHelper.accessor('presentation_guide_url', {
      cell: (info) => {
        const { use_new_builder: newBuilder } = info.row.original;
        if (newBuilder) return <Muted>N/A</Muted>;
        const url = info.getValue();
        return url
          ? <a className={linkClass} href={url} rel="noreferrer" target="_blank">View PDF</a>
          : <Muted>None yet</Muted>;
      },
      enableSorting: false,
      header: 'PDF',
      size: 90,
    }),
    columnHelper.display({
      cell: info => <LessonGuideActions guide={info.row.original} parent={parent} programId={programId} />,
      enableSorting: false,
      header: 'Actions',
      id: 'actions',
      meta: { align: 'right' },
      size: 110,
    }),
  ];
};

export default getLessonGuideColumns;
